// Pricing screen — ports pricing.js loadPricing/_renderPricingContent/_handlePayment.
// Plans + intro load via TanStack Query (replaces the manual _pricingLoaded flag
// and Promise.all). Payment click creates a link and redirects the tab.

import {
  IconClock,
  IconBrain,
  IconHeartHandshake,
  IconMicrophone,
  IconMessageHeart,
  IconYoga,
  IconUserHeart,
  IconBulb,
  IconFileText,
  IconChartLine,
} from '@tabler/icons-react'
import type { PlanId } from '../../payments/types'
import { discountPercent, formatDuration } from '../../lib/pricing'
import type { Plan } from '../../types/api'
import styles from './Pricing.module.css'
import { BreakUpPricing, usePricing } from '../../features/pricing'
import { useAnalyticsView } from '../../lib/analytics'

const PLAN_FEATURE_COPY = {
  session: ['Попробовать бота', '8 000 токенов', 'Без ограничения по времени'],
  month: ['Всё для системной работы', 'Психолог, инсайты, отчёт', 'Безлимитные сессии'],
  quarter: ['Всё из тарифа «Месяц»', '90 дней · автопродление', 'Навык закрепляется прочнее'],
}

function planFeatures(plan: Plan) {
  const copy = PLAN_FEATURE_COPY[plan.id as keyof typeof PLAN_FEATURE_COPY]
  if (copy) return copy
  if (plan.token_based === true) {
    const tokenCount = plan.tokens ?? 0
    const tokens = tokenCount >= 1000 ? `${(tokenCount / 1000).toFixed(0)}K` : tokenCount
    return [`${tokens} токенов`, 'Без ограничения по времени', 'Голосовые сообщения']
  }
  return ['Безлимитные сессии', 'Голосовые сообщения']
}

const HERO_PILLS = [
  { Icon: IconClock, label: 'Доступен 24/7' },
  { Icon: IconBrain, label: 'КПТ и ДПТ' },
  { Icon: IconHeartHandshake, label: '11 практик самопомощи' },
  { Icon: IconMicrophone, label: 'Анализ по тексту и голосу' },
]

const INCLUDED_FEATURES = [
  {
    Icon: IconMessageHeart,
    title: 'AI-психолог 24/7',
    body: 'Живой терапевтический диалог по протоколам КПТ и ДПТ, анализ эмоций по тексту и голосу.',
  },
  {
    Icon: IconYoga,
    title: '11 практик самопомощи',
    body: 'Дыхание, заземление, КПТ-дневник, работа с искажениями, поведенческая активация — под ваше состояние.',
  },
  {
    Icon: IconUserHeart,
    title: 'Поддержка живого психолога',
    body: 'Связь со специалистом внутри приложения, когда одного бота недостаточно.',
  },
  {
    Icon: IconBulb,
    title: 'Еженедельные инсайты',
    body: 'Разбор преобладающих эмоций, динамики и персональные рекомендации — раз в период.',
  },
  {
    Icon: IconFileText,
    title: 'Итог для сессии',
    body: 'Бот готовит сводку вашей работы — психолог входит в сессию уже с контекстом, вы не тратите время на пересказ.',
  },
  {
    Icon: IconChartLine,
    title: 'Анализ динамики',
    body: 'Видно, как меняется ваше состояние со временем и что работает лучше всего.',
  },
]

export default function Pricing() {
  const { channel, profile, profileError, retryProfile, data, isLoading, isError, payError, payNotice, payingId, handlePay } = usePricing()
  const isBreakUp = profile?.segment === 'break_up'

  useAnalyticsView('pricingViewed', isBreakUp && !profileError && !isLoading && !isError && Boolean(data?.plans.length))

  if (profileError || isError) {
    return (
      <div className={styles.wrap}>
        <div className={styles.error} role="alert">
          <div className={styles.errorIcon}>⚠️</div>
          <h2>Ошибка загрузки</h2>
          <p>{profileError || 'Не удалось загрузить тарифные планы'}</p>
          {profileError && <button type="button" className={styles.payBtn} onClick={retryProfile}>Повторить</button>}
        </div>
      </div>
    )
  }

  if (isLoading || !profile) return <div className={styles.wrap}><p className={styles.muted} role="status">Загрузка тарифов...</p></div>

  if (!data) return null
  if (profile.segment === 'break_up') {
    return <BreakUpPricing data={data} provider={channel.provider} payingId={payingId} onPay={handlePay} payError={payError} payNotice={payNotice} />
  }
  const plans = [...data.plans].sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
  const featuredPlan = plans.find((p) => p.featured)
  const productsByPlan = new Map(data.products.map((product) => [product.planId, product]))

  return (
    <div className={styles.wrap}>
      <div className={styles.hero}>
        <h1 className={styles.heroTitle}>
          AI-психолог рядом каждый день — и живой специалист, когда нужно
        </h1>
        <p className={styles.heroBody}>
          Круглосуточная поддержка на основе доказательных методик КПТ и ДПТ. Бот помогает справляться с тревогой, стрессом и подавленностью, а в важный момент подключается живой психолог — уже с контекстом о вашей работе.
        </p>
        <div className={styles.pills}>
          {HERO_PILLS.map((pill) => (
            <span key={pill.label} className={styles.pill}>
              <pill.Icon size={16} className={styles.pillIcon} />
              {pill.label}
            </span>
          ))}
        </div>
      </div>

      <div className={styles.cards}>
        {plans.map((plan) => {
          const discount = channel.provider === 'rustore' ? null : discountPercent(plan, featuredPlan)
          const product = productsByPlan.get(plan.id as PlanId)
          const nativeUnavailable = channel.provider === 'rustore' && !product
          const displayName = channel.provider === 'rustore' ? (product?.title ?? 'Тариф недоступен') : plan.name
          const displayPrice = channel.provider === 'rustore'
            ? (product?.formattedPrice ?? 'Недоступно')
            : `${plan.price?.toLocaleString()} ₽`
          return (
            <div key={plan.id} className={`${styles.card} ${plan.featured ? styles.featured : ''}`}>
              {plan.featured && <div className={styles.popular}>Популярный</div>}
              <div className={styles.name}>{displayName}</div>
              {plan.featured && (
                <div className={styles.subtitle}>
                  {formatDuration(plan.duration_days)} · {plan.recurring ? 'автопродление' : 'разовый платёж'}
                </div>
              )}
              {discount != null && (
                <span className={`${styles.badge} ${styles.badgeDiscount}`}>Выгоднее на {discount}%</span>
              )}
              {discount == null && !plan.featured && (
                <span className={`${styles.badge} ${plan.recurring ? '' : styles.badgeOnce}`}>
                  {plan.recurring ? 'Автопродление' : 'Разовый платеж'}
                </span>
              )}
              <div className={styles.price}>
                {displayPrice}
              </div>
              <ul className={styles.features}>
                {planFeatures(plan).map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <button
                className={`${styles.payBtn} ${plan.featured ? '' : styles.payBtnSecondary}`}
                value={plan.id}
                onClick={handlePay}
                disabled={payingId != null || nativeUnavailable}
              >
                {nativeUnavailable ? 'Недоступно' : payingId === plan.id ? 'Оплата...' : 'Оплатить'}
              </button>
              {plan.featured && plan.recurring && (
                <p className={styles.cancelNote}>Отмена в один шаг, в Настройках</p>
              )}
            </div>
          )
        })}
      </div>

      {data.catalogError && <p className={styles.error} role="alert">{data.catalogError}</p>}
      {payError && <p className={styles.error} role="alert">{payError}</p>}
      {payNotice && <p className={styles.muted} role="status">{payNotice}</p>}

      <div className={styles.included}>
        <p className={styles.includedTitle}>
          Что входит в «Месяц» и «Квартал»
        </p>
        <p className={styles.includedSub}>Полная система поддержки, а не только чат с ботом</p>
        <div className={styles.includedGrid}>
          {INCLUDED_FEATURES.map((f) => (
            <div key={f.title} className={styles.includedItem}>
              <f.Icon size={22} className={styles.includedIcon} />
              <div>
                <p className={styles.includedItemTitle}>{f.title}</p>
                <p className={styles.includedItemBody}>{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
